import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { ChannelMemberResponse, Channel } from 'stream-chat'
import { BaseChatPackage } from '../utils'

export type MentionUser = {
  id: string
  name: string
  image?: string
}

type MentionState = {
  isActive: boolean
  query: string
  startPosition: number
}

type UseMentionSuggestionsProps = {
  channel?: Channel
  text: string
  cursorPosition: number
}

const membersCache = new Map<string, ChannelMemberResponse[]>()
const loadingChannels = new Set<string>()

export class UseMentionSuggestions extends BaseChatPackage {
  useMentionSuggestions({
    channel,
    text,
    cursorPosition,
  }: UseMentionSuggestionsProps) {
    const [members, setMembers] = useState<ChannelMemberResponse[]>([])
    const [isLoadingMembers, setIsLoadingMembers] = useState(false)
    const [mentionState, setMentionState] = useState<MentionState>({
      isActive: false,
      query: '',
      startPosition: 0,
    })
    const loadAttemptedRef = useRef<string | null>(null)

    const hasAtSymbol = useMemo(() => {
      if (!text) return false
      const textBeforeCursor = text.slice(0, cursorPosition)
      return textBeforeCursor.includes('@')
    }, [text, cursorPosition])

    useEffect(() => {
      if (!channel?.id) return
      
      const channelId = channel.id
      
      if (membersCache.has(channelId)) {
        setMembers(membersCache.get(channelId)!)
        return
      }
    }, [channel?.id])

    useEffect(() => {
      if (!channel?.id || !hasAtSymbol) return
      
      const channelId = channel.id
      
      if (membersCache.has(channelId)) {
        setMembers(membersCache.get(channelId)!)
        return
      }
      
      if (loadingChannels.has(channelId)) {
        return
      }

      const loadMembers = async () => {
        loadingChannels.add(channelId)
        setIsLoadingMembers(true)
        
        try {
          const chatClient = await this.getChatClient()
          const currentUserId = chatClient?.user?.id

          const response = await channel.queryMembers({}, { user_id: -1 }, { limit: 100 })

          const filteredMembers = response.members.filter(
            member => member.user_id !== currentUserId
          )

          membersCache.set(channelId, filteredMembers)
          setMembers(filteredMembers)
        } catch (error) {
          console.warn('Error fetching channel members:', error)
          loadingChannels.delete(channelId)
        } finally {
          setIsLoadingMembers(false)
          loadingChannels.delete(channelId)
        }
      }
      
      loadMembers()
    }, [hasAtSymbol, channel?.id, channel, this.getChatClient])

    useEffect(() => {
      if (!text) {
        setMentionState({ isActive: false, query: '', startPosition: 0 })
        return
      }

      const textBeforeCursor = text.slice(0, cursorPosition)
      const lastAtIndex = textBeforeCursor.lastIndexOf('@')

      if (lastAtIndex === -1) {
        setMentionState({ isActive: false, query: '', startPosition: 0 })
        return
      }

      const charBeforeAt = lastAtIndex > 0 ? text[lastAtIndex - 1] : ' '
      const isValidMentionStart = charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0

      if (!isValidMentionStart) {
        setMentionState({ isActive: false, query: '', startPosition: 0 })
        return
      }

      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1)

      if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
        setMentionState({ isActive: false, query: '', startPosition: 0 })
        return
      }

      setMentionState({
        isActive: true,
        query: textAfterAt.toLowerCase(),
        startPosition: lastAtIndex,
      })
    }, [text, cursorPosition])

    const suggestions = useMemo<MentionUser[]>(() => {
      if (!mentionState.isActive || isLoadingMembers) return []

      const { query } = mentionState

      return members
        .filter(member => {
          if (!member.user) return false
          const name = member.user.name?.toLowerCase() || ''
          const id = member.user.id?.toLowerCase() || ''
          return name.includes(query) || id.includes(query)
        })
        .map(member => ({
          id: member.user?.id || '',
          name: member.user?.name || member.user?.id || '',
          image: member.user?.image as string | undefined,
        }))
    }, [members, mentionState, isLoadingMembers])

    const selectMention = useCallback((user: MentionUser): string => {
      const { startPosition } = mentionState
      const beforeMention = text.slice(0, startPosition)
      const afterCursor = text.slice(cursorPosition)
      const mentionText = `@${user.name} `

      return `${beforeMention}${mentionText}${afterCursor}`
    }, [text, cursorPosition, mentionState])

    const extractMentionedUsers = useCallback((messageText: string): MentionUser[] => {
      const mentionedUsers: MentionUser[] = []
      const mentionRegex = /@([a-zA-ZÀ-ÿ0-9_]+(?:\s+[a-zA-ZÀ-ÿ0-9_]+)*)/g
      let match

      while ((match = mentionRegex.exec(messageText)) !== null) {
        const mentionName = match[1].toLowerCase()

        const foundMember = members.find(member => {
          const name = member.user?.name?.toLowerCase() || ''
          return name === mentionName
        })

        if (foundMember?.user) {
          const alreadyAdded = mentionedUsers.some(u => u.id === foundMember.user?.id)
          if (!alreadyAdded) {
            mentionedUsers.push({
              id: foundMember.user.id || '',
              name: foundMember.user.name || foundMember.user.id || '',
              image: foundMember.user.image as string | undefined,
            })
          }
        }
      }

      return mentionedUsers
    }, [members])

    const closeSuggestions = useCallback(() => {
      setMentionState({ isActive: false, query: '', startPosition: 0 })
    }, [])

    return {
      suggestions,
      isActive: mentionState.isActive && suggestions.length > 0,
      selectMention,
      extractMentionedUsers,
      closeSuggestions,
      isLoadingMembers,
    }
  }
}
